/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { NotesGrid } from 'app/core/components/notes/notes-grid';
import { INoteSearchFilter } from 'app/core/services/note-service';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class CaseNotesPage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    private caseId: string | null = null;

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        moduleCode: 'CM',
        addTargetProperty: this.addCaseProperty.bind(this),
        excludeSystem: false,
        includeRollup: true
    };

    protected searchFilters: INoteSearchFilter[] = [
        {
            i18nKey: 'cm:case.$',
            filterValue: 'CM.Case',
            selected: this.env.config.pageConfigurations.cm.case.notes.caseChecked
        },
        {
            i18nKey: 'cm:carePlanBarrier.$',
            filterValue: 'CM.CarePlanBarrier',
            selected: this.env.config.pageConfigurations.cm.case.notes.barrierChecked
        },
        {
            i18nKey: 'cm:carePlanGoal.$',
            filterValue: 'CM.CarePlanGoal',
            selected: this.env.config.pageConfigurations.cm.case.notes.goalChecked
        },
        {
            i18nKey: 'cm:carePlanIntervention.$',
            filterValue: 'CM.CarePlanIntervention',
            selected: this.env.config.pageConfigurations.cm.case.notes.interventionChecked
        },
        {
            i18nKey: 'cm:carePlanOutcome.$',
            filterValue: 'CM.CarePlanOutcome',
            selected: this.env.config.pageConfigurations.cm.case.notes.outcomeChecked
        },
        {
            i18nKey: 'cm:task.$',
            filterValue: 'CM.CaseTask',
            selected: this.env.config.pageConfigurations.cm.case.notes.taskChecked
        }
    ];

    private subscriptions: Array<Subscription> = [];

    ////****************** Element References ***************************************////
    protected $notesGrid!: NotesGrid;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected async canActivate(params: { caseId: string }): Promise<boolean> {
        if(!params.caseId) { throw new Error('Missing or invalid caseId parameter'); }

        return true;
    }

    protected async activate(params: { caseId: string }): Promise<void> {
        if(this.caseId !== params.caseId) { this.caseId = params.caseId; }

        await this.accountContext.load();

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ****************************************************////
    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {
            this.reload();
        };
    }

    ////****************** Functions *************************************************////
    public async reload(): Promise<void> {
        if(this.caseId && this.$notesGrid) {
            this.$notesGrid.reload();
        }
    }

    private addCaseProperty(note: CM.CaseNote): void {
        note.case = { id: this.caseId };
    }
}
